import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, Package, AlertTriangle, ArrowDownToLine, ArrowUpFromLine, Settings2, History, Search,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { EmptyState, Section, StatCard } from "@/components/Primitives";
import { RpSelect } from "@/components/RpSelect";
import { StaggerRoot, StaggerItem } from "@/components/motion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const UNITS = [
  { id: "kg", dari: "کیلوگرام", pashto: "کیلو" },
  { id: "g", dari: "گرام", pashto: "ګرام" },
  { id: "liter", dari: "لیتر", pashto: "لیټر" },
  { id: "ml", dari: "میلی‌لیتر", pashto: "میلي لیټر" },
  { id: "piece", dari: "عدد", pashto: "عدد" },
  { id: "box", dari: "کارتن", pashto: "کارټن" },
  { id: "bag", dari: "بوجی", pashto: "بوجۍ" },
];

const TX_TYPES = [
  { id: "in", icon: ArrowDownToLine, key: "stockIn", color: "#16A34A", bg: "#ECFDF5" },
  { id: "out", icon: ArrowUpFromLine, key: "stockOut", color: "#DC2626", bg: "#FEF2F2" },
  { id: "adjust", icon: Settings2, key: "stockAdjust", color: "#F59E0B", bg: "#FFFBEB" },
];

export default function Inventory() {
  const { t, lang, tr, money } = useI18n();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [showLowOnly, setShowLowOnly] = useState(false);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({
    name_dari: "", name_pashto: "", unit: "kg",
    quantity: 0, min_quantity: 0, cost_per_unit: 0, supplier: "",
  });
  const [txOpen, setTxOpen] = useState(false);
  const [txItem, setTxItem] = useState(null);
  const [txForm, setTxForm] = useState({ type: "in", quantity: "", notes: "" });
  const [history, setHistory] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/inventory/items");
      setItems(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items
    .filter((it) => showLowOnly ? it.quantity <= it.min_quantity : true)
    .filter((it) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return it.name_dari.toLowerCase().includes(q) || it.name_pashto.toLowerCase().includes(q);
    });

  const lowCount = items.filter((it) => it.quantity <= it.min_quantity).length;
  const totalValue = items.reduce((s, it) => s + (it.quantity || 0) * (it.cost_per_unit || 0), 0);

  const unitLabel = (u) => {
    const f = UNITS.find((x) => x.id === u);
    return f ? (lang === "pashto" ? f.pashto : f.dari) : u;
  };

  const save = async () => {
    if (!form.name_dari) { toast.error(t("error")); return; }
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity),
        min_quantity: Number(form.min_quantity),
        cost_per_unit: Number(form.cost_per_unit),
      };
      if (edit) await api.put(`/inventory/items/${edit.id}`, payload);
      else await api.post("/inventory/items", payload);
      toast.success(t("success"));
      setOpen(false); setEdit(null);
      setForm({ name_dari: "", name_pashto: "", unit: "kg", quantity: 0, min_quantity: 0, cost_per_unit: 0, supplier: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitTx = async () => {
    if (!txItem || !Number(txForm.quantity)) { toast.error(t("error")); return; }
    try {
      await api.post("/inventory/transactions", {
        stock_item_id: txItem.id,
        type: txForm.type,
        quantity: Number(txForm.quantity),
        notes: txForm.notes,
      });
      toast.success(t("success"));
      setTxOpen(false); setTxItem(null);
      setTxForm({ type: "in", quantity: "", notes: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const showHistory = async (it) => {
    try {
      const r = await api.get("/inventory/transactions", { params: { stock_item_id: it.id } });
      setHistory({ item: it, transactions: r.data });
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/inventory/items/${id}`);
      toast.success(t("success"));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <StaggerRoot className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading">{t("inventory")}</h1>
          <p className="text-sm text-gray-500 mt-1">{items.length} {t("stockItem")}</p>
        </div>
        <button
          data-testid="add-stock-item"
          onClick={() => { setEdit(null); setForm({ name_dari: "", name_pashto: "", unit: "kg", quantity: 0, min_quantity: 0, cost_per_unit: 0, supplier: "" }); setOpen(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#071A33] text-white text-sm font-medium hover:bg-[#7F1020]"
        >
          <Plus className="w-4 h-4" /> {t("addStock")}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={Package} label={t("stockItem")} value={items.length} accent="#071A33" />
        <StatCard icon={AlertTriangle} label={t("lowStock")} value={lowCount} accent="#DC2626" />
        <StatCard icon={Package} label={t("balance")} value={money(totalValue)} accent="#16A34A" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="w-full h-11 pr-10 pl-4 rounded-xl border border-gray-200 focus:outline-none focus:border-[#071A33]" />
        </div>
        <button onClick={() => setShowLowOnly(!showLowOnly)} className={`px-4 py-2 rounded-xl text-sm border ${showLowOnly ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-white text-gray-700 border-gray-200"}`}>
          <AlertTriangle className="w-4 h-4 inline-block ml-1" /> {t("lowStock")}
        </button>
      </div>

      <Section title={t("inventory")}>
        {filtered.length === 0 ? <EmptyState icon={Package} title={t("empty")} /> : (
          <div className="overflow-x-auto thin-scroll">
            <table className="w-full text-right text-sm">
              <thead className="text-xs text-gray-500 bg-gray-50">
                <tr>
                  <th className="p-3">{t("name")}</th>
                  <th className="p-3">{t("unit")}</th>
                  <th className="p-3">{t("currentQty")}</th>
                  <th className="p-3">{t("minQty")}</th>
                  <th className="p-3">{t("costPerUnit")}</th>
                  <th className="p-3">{t("balance")}</th>
                  <th className="p-3">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => {
                  const low = it.quantity <= it.min_quantity;
                  return (
                    <tr key={it.id} className={`border-t border-gray-100 ${low ? "bg-rose-50/40" : ""}`}>
                      <td className="p-3 text-gray-900 font-medium">
                        <div className="flex items-center gap-2">
                          {low && <span className="status-dot bg-rose-500 animate-pulse" />}
                          {tr(it, "name")}
                        </div>
                      </td>
                      <td className="p-3 text-gray-500">{unitLabel(it.unit)}</td>
                      <td className={`p-3 tabular font-bold ${low ? "text-rose-700" : "text-gray-900"}`}>{it.quantity}</td>
                      <td className="p-3 tabular text-gray-500">{it.min_quantity}</td>
                      <td className="p-3 tabular">{money(it.cost_per_unit)}</td>
                      <td className="p-3 tabular text-[#071A33] font-medium">{money((it.quantity || 0) * (it.cost_per_unit || 0))}</td>
                      <td className="p-3 flex gap-1">
                        <button data-testid={`stock-tx-${it.id}`} onClick={() => { setTxItem(it); setTxForm({ type: "in", quantity: "", notes: "" }); setTxOpen(true); }} className="px-2.5 py-1.5 rounded-md bg-[#071A33]/10 text-[#071A33] text-xs font-medium hover:bg-[#071A33]/20 inline-flex items-center gap-1">
                          <ArrowDownToLine className="w-3 h-3" /> {t("addTx")}
                        </button>
                        <button onClick={() => showHistory(it)} className="p-1.5 rounded-md border border-gray-200 hover:bg-gray-50"><History className="w-3.5 h-3.5" /></button>
                        <button onClick={() => { setEdit(it); setForm({ name_dari: it.name_dari, name_pashto: it.name_pashto, unit: it.unit, quantity: it.quantity, min_quantity: it.min_quantity, cost_per_unit: it.cost_per_unit, supplier: it.supplier || "" }); setOpen(true); }} className="p-1.5 rounded-md border border-gray-200 hover:bg-gray-50"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => del(it.id)} className="p-1.5 rounded-md border border-rose-100 text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Add/Edit item */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit ? t("edit") : t("addStock")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-1">
              <label className="text-xs text-gray-500 mb-1 block">{t("name")} (دری)</label>
              <input data-testid="stock-name-dari" value={form.name_dari} onChange={(e) => setForm({ ...form, name_dari: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t("name")} (پښتو)</label>
              <input value={form.name_pashto} onChange={(e) => setForm({ ...form, name_pashto: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t("unit")}</label>
              <RpSelect
                value={form.unit}
                onValueChange={(v) => setForm({ ...form, unit: v })}
                options={UNITS.map((u) => ({
                  value: u.id,
                  label: lang === "pashto" ? u.pashto : u.dari,
                }))}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t("supplier")}</label>
              <input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t("currentQty")}</label>
              <input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200 tabular" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">{t("minQty")}</label>
              <input type="number" value={form.min_quantity} onChange={(e) => setForm({ ...form, min_quantity: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200 tabular" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-500 mb-1 block">{t("costPerUnit")} (AFN)</label>
              <input type="number" value={form.cost_per_unit} onChange={(e) => setForm({ ...form, cost_per_unit: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-gray-200 tabular" />
            </div>
          </div>
          <DialogFooter className="flex-row-reverse">
            <button data-testid="save-stock-item" onClick={save} className="px-5 py-2.5 rounded-xl bg-[#071A33] text-white">{t("save")}</button>
            <button onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-xl border border-gray-200">{t("cancel")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transaction */}
      <Dialog open={txOpen} onOpenChange={setTxOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader><DialogTitle>{t("addTx")}: {txItem ? tr(txItem, "name") : ""}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {TX_TYPES.map((tp) => {
                const Icon = tp.icon;
                const active = txForm.type === tp.id;
                return (
                  <button
                    key={tp.id}
                    onClick={() => setTxForm({ ...txForm, type: tp.id })}
                    data-testid={`tx-type-${tp.id}`}
                    className={`p-3 rounded-xl border text-xs font-medium flex flex-col items-center gap-1 ${active ? "border-[#071A33] bg-[#071A33]/5" : "border-gray-200 bg-white"}`}
                    style={active ? { color: tp.color } : {}}
                  >
                    <Icon className="w-5 h-5" style={{ color: tp.color }} />
                    {t(tp.key)}
                  </button>
                );
              })}
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">
                {t("quantity")} {txItem ? `(${unitLabel(txItem.unit)})` : ""}
                {txForm.type === "adjust" && <span className="text-amber-600 mr-2">{lang === "pashto" ? "(نوې موجودي)" : "(موجودی جدید)"}</span>}
              </label>
              <input data-testid="tx-qty" type="number" value={txForm.quantity} onChange={(e) => setTxForm({ ...txForm, quantity: e.target.value })} className="w-full h-12 px-4 rounded-xl border border-gray-200 text-lg tabular font-bold" />
            </div>
            <textarea value={txForm.notes} onChange={(e) => setTxForm({ ...txForm, notes: e.target.value })} placeholder={t("notes")} className="w-full p-3 rounded-xl border border-gray-200 min-h-[60px]" />
          </div>
          <DialogFooter className="flex-row-reverse">
            <button data-testid="save-tx" onClick={submitTx} className="px-5 py-2.5 rounded-xl bg-[#071A33] text-white">{t("save")}</button>
            <button onClick={() => setTxOpen(false)} className="px-5 py-2.5 rounded-xl border border-gray-200">{t("cancel")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History */}
      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent dir="rtl" className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{t("txHistory")}: {history?.item ? tr(history.item, "name") : ""}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto thin-scroll">
            {history?.transactions?.length === 0 && <EmptyState title={t("empty")} />}
            {history?.transactions?.map((tx) => {
              const conf = TX_TYPES.find((x) => x.id === tx.type);
              const Icon = conf?.icon || ArrowDownToLine;
              return (
                <div key={tx.id} className="flex justify-between items-center border-b border-gray-100 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl grid place-items-center" style={{ background: conf?.bg, color: conf?.color }}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-sm font-medium">{t(conf?.key || "addTx")}</div>
                      <div className="text-xs text-gray-500">{(tx.date || "").slice(0, 16).replace("T", " ")} • {tx.created_by_name}</div>
                      {tx.notes && <div className="text-xs text-gray-500 mt-0.5">{tx.notes}</div>}
                    </div>
                  </div>
                  <div className="text-left">
                    <div className={`font-bold tabular ${tx.type === "in" ? "text-emerald-600" : tx.type === "out" ? "text-rose-600" : "text-amber-600"}`}>
                      {tx.type === "in" ? "+" : tx.type === "out" ? "-" : "="} {tx.quantity}
                    </div>
                    <div className="text-xs text-gray-400 tabular">{t("balance")}: {tx.balance_after}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </StaggerRoot>
  );
}
